import { useEffect, useState } from 'react';
import { Text } from '@savvy-kit/foundations/text';
import { graphql } from 'src/gql';

import { XSavvyFooter } from '@savvy/deprecated-shared/x-components/x-savvy-footer';
import { XSavvyLogoHeader } from '@savvy/deprecated-shared/x-components/x-savvy-logo-header';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import { TrackedButton } from '../tracked';

const CheckYourEmailMutation = graphql(/* GraphQL */ `
  mutation CheckYourEmail($input: ResendEmailInput!) {
    resendEmail(input: $input) {
      success
    }
  }
`);

type Props = {
  readonly email: string;
};

export function CheckYourEmail({ email }: Props) {
  const [resendEmail, { loading }] = useMutationWithErrorLogging(CheckYourEmailMutation);
  const [secondsUntilResend, setSecondsUntilResend] = useState(10);
  // Timer value is only set (so cleanup can clear it via the closure) and never read.
  // eslint-disable-next-line react/hook-use-state -- value intentionally discarded
  const [, setTimer] = useState<NodeJS.Timeout>();

  useEffect(() => {
    const newTimer = setInterval(() => {
      setSecondsUntilResend((prev) => Math.max(0, prev - 1));
    }, 1000);
    setTimer(newTimer);
    return () => clearInterval(newTimer);
  }, []);

  const handleResendEmail = async () => {
    await resendEmail({
      variables: { input: { email, emailType: 'create_password' } },
    });
    setSecondsUntilResend(10);
  };

  return (
    <div className="flex flex-col min-h-screen p-8">
      <XSavvyLogoHeader />
      <main className="mx-auto w-full max-w-[600px] px-4 sm:px-6 grow py-8 flex items-center">
        <div className="flex flex-col items-center pb-16 w-full gap-8">
          <Text variant="h2">Check your email</Text>
          <Text variant="body">We sent you a link to verify your email address.</Text>
          <div className="flex flex-col items-center gap-2">
            <TrackedButton
              elementName="check-your-email-resend-email-button"
              isLoading={loading}
              disabled={secondsUntilResend > 0}
              onClick={handleResendEmail}
            >
              Resend email
            </TrackedButton>
            {secondsUntilResend > 0 && (
              <Text variant="caption" color="secondary">
                Resend available in {secondsUntilResend} seconds
              </Text>
            )}
          </div>
        </div>
      </main>
      <XSavvyFooter />
    </div>
  );
}
